import type { ComponentType } from "react";
import { FileText, Send, Settings2, Wallet } from "lucide-react";
import { FREQUENCIA_LABELS } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { reais } from "@/lib/dinheiro";
import { cn } from "@/lib/cn";

export type AbaDaCobranca = "faturar" | "extratos" | "configuracoes";

type Atalho = {
  icone: ComponentType<{ className?: string }>;
  titulo: string;
  valor: string;
  detalhe: string;
  detalheCor?: string;
  destaque?: boolean;
  /** Cartão que abre uma lista logo abaixo: marcado quando ela está aberta. */
  selecionado?: boolean;
  onClick?: () => void;
};

function Cartao({ icone: Icone, titulo, valor, detalhe, detalheCor, destaque, selecionado, onClick }: Atalho) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      aria-pressed={selecionado}
      className={cn(
        "flex min-w-0 flex-col gap-2 rounded-xl border p-3 text-left shadow-sm transition-colors sm:p-4",
        destaque
          ? "border-brand-600 bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-60"
          : selecionado
            ? "border-brand-600 bg-brand-50/60 ring-1 ring-brand-600 dark:border-primary dark:bg-primary/10 dark:ring-primary"
            : "border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50/60 dark:border-border dark:bg-card dark:hover:bg-muted/40",
      )}
    >
      <span className="flex items-center gap-2">
        <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", destaque ? "bg-white/15" : "bg-brand-50 text-brand-600 dark:bg-primary/10 dark:text-primary")}>
          <Icone className="h-4 w-4" />
        </span>
        <span className={cn("truncate text-[13px] font-semibold", !destaque && "text-zinc-700 dark:text-foreground")}>{titulo}</span>
      </span>
      <span className={cn("truncate text-base font-bold tabular-nums sm:text-xl", !destaque && "text-zinc-900 dark:text-foreground")}>{valor}</span>
      <span className={cn("truncate text-[11px] sm:text-xs", destaque ? "text-white/80" : (detalheCor ?? "text-zinc-500 dark:text-muted-foreground"))}>{detalhe}</span>
    </button>
  );
}

/** Os ícones do topo de Cobranças, cada um com o seu número. "A faturar" e "Em aberto" são também as
 *  abas da lista de baixo. */
export function AtalhosDaCobranca({ onFechar, onIr, lista }: { onFechar: (() => void) | null; onIr: (aba: AbaDaCobranca) => void; lista: AbaDaCobranca }) {
  const preparo = trpc.financeiro.envio.preparar.useQuery().data;
  const aFaturar = trpc.financeiro.extratos.aFaturar.useQuery().data;
  const extratos = trpc.financeiro.extratos.listar.useQuery().data;
  const config = trpc.financeiro.configExtrato.ler.useQuery().data;

  const prontos = (preparo?.aEmitir.length ?? 0) + (preparo?.aEnviar.length ?? 0);
  const prontoCents = (preparo?.aEmitir ?? []).reduce((t, d) => t + d.prontoCents, 0) + (preparo?.aEnviar ?? []).reduce((t, e) => t + e.totalCents, 0);
  const abertos = (extratos ?? []).filter((e) => e.status === "EMITIDO" && e.saldoCents > 0);
  const vencido = abertos.filter((e) => e.situacao === "VENCIDO").reduce((t, e) => t + e.saldoCents, 0);
  const destinatarios = aFaturar?.destinatarios ?? [];
  const frequencia = config?.frequencia ? FREQUENCIA_LABELS[config.frequencia as keyof typeof FREQUENCIA_LABELS] : "—";
  const fechamento = config?.frequencia === "MENSAL" ? ` · fecha dia ${config.diaDeFechamento}` : "";

  return (
    <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
      <Cartao
        destaque
        icone={Send}
        titulo="Fechar e enviar"
        valor={reais(prontoCents)}
        detalhe={prontos ? `${prontos} ${prontos === 1 ? "extrato pronto" : "extratos prontos"} para mandar` : "Nada pronto agora"}
        onClick={onFechar && prontos ? onFechar : undefined}
      />
      <Cartao
        icone={FileText}
        titulo="A faturar"
        valor={reais(destinatarios.reduce((t, d) => t + d.totalCents, 0))}
        detalhe={`${destinatarios.length} ${destinatarios.length === 1 ? "cliente" : "clientes"} com entrega sem extrato`}
        selecionado={lista === "faturar"}
        onClick={() => onIr("faturar")}
      />
      <Cartao
        icone={Wallet}
        titulo="Em aberto"
        valor={reais(abertos.reduce((t, e) => t + e.saldoCents, 0))}
        detalhe={vencido ? `${reais(vencido)} vencido` : "Nada vencido"}
        detalheCor={vencido ? "font-semibold text-red-600" : undefined}
        selecionado={lista === "extratos"}
        onClick={() => onIr("extratos")}
      />
      <Cartao icone={Settings2} titulo="Configurações" valor={frequencia} detalhe={`Cobrança padrão${fechamento}`} onClick={() => onIr("configuracoes")} />
    </div>
  );
}
